import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ArrowRight, X, Sparkles, Hourglass } from 'lucide-react';
import { apiGet, apiPost } from '../lib/api.js';

/**
 * SetupChecklist — first-login walkthrough card for new agents/admins.
 *
 * Mounts on the Dashboard. Polls /api/onboarding/checklist for current
 * progress. Auto-hides when:
 *   - all REQUIRED items are done (percent === 100), OR
 *   - the user dismissed it (server-side flag).
 *
 * Each item links to the page where they finish that step. As they hit
 * milestones (claim a number, add billing, etc.), the next refetch flips
 * the item to "done" automatically — no manual marking required.
 */
export default function SetupChecklist() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['onboarding', 'checklist'],
    queryFn: () => apiGet('/onboarding/checklist'),
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    staleTime: 30_000,
  });

  const dismiss = useMutation({
    mutationFn: () => apiPost('/onboarding/checklist/dismiss', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding', 'checklist'] }),
  });

  if (isLoading || !data?.ok) return null;

  // Waiting on the manager: show an informative card instead of a checklist
  // the agent can't act on yet.
  if (data.pendingApproval) {
    return (
      <div className="panel p-5 mb-6 border border-amber-500/30 bg-amber-500/5 flex items-start gap-3">
        <Hourglass className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
        <div>
          <div className="font-display text-lg font-bold">Waiting for your manager's approval</div>
          <p className="text-sm text-white/60 mt-1">
            Your account is created. Once your team manager approves you, your setup steps appear here
            and you'll be joined to the team's messaging campaign automatically.
          </p>
        </div>
      </div>
    );
  }

  // Hidden once the agent dismisses it. At 100% it stays (with a celebration
  // banner) until they choose to dismiss, so they see the win first.
  const allRequiredDone = data.percent === 100;
  if (data.dismissed) return null;

  const items = data.items || [];

  return (
    <div className="panel p-5 mb-6 border border-civix-purple/30 bg-linear-to-br from-civix-purple/6 to-civix-cyan/4 relative overflow-hidden">
      {/* Decorative glow */}
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-civix-purple/20 blur-3xl pointer-events-none" />

      <div className="relative">
        {allRequiredDone && (
          <div
            role="status"
            className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3"
          >
            <div className="text-sm font-medium text-emerald-200">
              🎉 You're all set! Your CRM, phone lines, digital business card, and manager templates are ready to go.
            </div>
            <button
              onClick={() => dismiss.mutate()}
              disabled={dismiss.isPending}
              className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold border border-emerald-400/40 text-emerald-100 hover:bg-emerald-500/20 transition"
            >
              Dismiss Checklist
            </button>
          </div>
        )}

        {/* Header row */}
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <div className="flex items-center gap-2 text-civix-purpleSoft/90 uppercase tracking-[0.3em] text-[10px]">
              <Sparkles className="w-3 h-3" />
              Get Started
            </div>
            <h2 className="font-display text-xl font-bold mt-1">
              Welcome to CIVIX — let's get you set up
            </h2>
            <p className="text-sm text-white/60 mt-1">
              Knock out these steps to start working leads. You can come back to this any time.
            </p>
          </div>
          <button
            onClick={() => dismiss.mutate()}
            className="p-1.5 rounded-md text-white/40 hover:text-white/80 hover:bg-white/5 transition"
            title="Hide checklist"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mb-5">
          <div className="flex items-center justify-between text-xs text-white/50 mb-1.5">
            <span>
              {data.completed} of {data.total} steps completed ({data.percent}%)
            </span>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-linear-to-r from-civix-purple to-civix-cyan transition-all duration-500"
              style={{ width: `${data.percent}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-2">
          {items.map((item) => (
            <ChecklistItem key={item.key} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ChecklistItem({ item }) {
  const Icon = item.done ? CheckCircle2 : Circle;
  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border transition ${
        item.done
          ? 'border-emerald-500/20 bg-emerald-500/4'
          : 'border-white/5 bg-white/2 hover:border-civix-purple/30'
      }`}
    >
      <Icon className={`w-5 h-5 shrink-0 ${item.done ? 'text-emerald-400' : 'text-white/30'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className={`font-medium text-sm ${item.done ? 'text-white/60' : 'text-white/90'}`}>
            {item.title}
          </div>
          {item.optional && (
            <span className="text-[10px] uppercase tracking-wider text-white/40 bg-white/5 px-1.5 py-0.5 rounded-sm">
              Optional
            </span>
          )}
        </div>
        <div className="text-xs text-white/50 mt-0.5">{item.description}</div>
      </div>
      <Link
        to={item.link}
        className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
          item.done
            ? 'border border-white/10 text-white/60 hover:text-white hover:border-white/30'
            : 'bg-civix-purple text-white hover:bg-civix-purple/80'
        }`}
      >
        {item.done ? 'Edit' : 'Start Step'}
        {!item.done && <ArrowRight className="w-3.5 h-3.5" />}
      </Link>
    </div>
  );
}
